import React, { useState, useEffect } from "react";
import { Save } from "lucide-react";

const OurFootPrintForm = ({ data = {}, onSave }) => {
  const [title, setTitle] = useState("");
  const [locations, setLocations] = useState([]);
  const MAX_LOCATIONS = 6;

  useEffect(() => {
    // Take existing locations (max 6)
    const existing = Array.isArray(data.locations)
      ? data.locations.slice(0, MAX_LOCATIONS).map((loc) => ({
          name: loc.name || "",
          description: loc.description || "",
        }))
      : [];

    // Pad with empty rows till we reach 6
    const padded = [
      ...existing,
      ...Array.from(
        { length: Math.max(0, MAX_LOCATIONS - existing.length) },
        () => ({ name: "", description: "" })
      ),
    ];

    setTitle(data.title || "");
    setLocations(padded);
  }, [data]);

  const handleLocationChange = (index, field, value) => {
    setLocations((prev) =>
      prev.map((loc, i) =>
        i === index ? { ...loc, [field]: value } : loc
      )
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!onSave) return;

    const cleanedLocations = locations
      .map((loc) => ({
        name: (loc.name || "").trim(),
        description: (loc.description || "").trim(),
      }))
      .filter((loc) => loc.name.length > 0);

    const payload = {
      title: (title || "").trim() || "Our Presence Across Locations",
      locations: cleanedLocations,
    };

    onSave(payload);
  };

  return (
    <div className="bg-white rounded-2xl shadow-md p-8 border border-gray-100">
      <h2 className="text-2xl font-semibold text-gray-900 mb-6">
        Our Footprint
      </h2>

      {/* Section Title */}
      <div className="mb-6">
        <label className="block text-base font-medium text-gray-700 mb-2">
          Section Title
        </label>
        <input
          type="text"
          placeholder="e.g. Our Footprint Across India"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-4 py-2 text-base focus:ring-2 focus:ring-blue-600 focus:border-blue-600 outline-none"
        />
      </div>

      {/* Locations Table */}
      <div className="overflow-x-auto border border-gray-200 rounded-xl">
        <table className="w-full text-base text-gray-800">
          <thead className="bg-gray-100 border-b border-gray-200">
            <tr>
              <th className="px-5 py-3 text-left font-semibold w-[8%]">No.</th>
              <th className="px-5 py-3 text-left font-semibold w-[25%]">
                Location Name
              </th>
              <th className="px-5 py-3 text-left font-semibold">
                Description 
              </th>
            </tr>
          </thead>
          <tbody>
            {locations.map((loc, index) => (
              <tr
                key={index}
                className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
              >
                <td className="px-5 py-3 text-gray-600 font-medium">
                  {index + 1}
                </td>
                <td className="px-5 py-3">
                  <input
                    type="text"
                    value={loc.name}
                    onChange={(e) =>
                      handleLocationChange(index, "name", e.target.value)
                    }
                    placeholder="City / Region"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-base focus:ring-1 focus:ring-blue-500 outline-none"
                  />
                </td>
                <td className="px-5 py-3">
                  <textarea
                    value={loc.description}
                    onChange={(e) =>
                      handleLocationChange(index, "description", e.target.value)
                    }
                    placeholder="Short description..."
                    rows={2}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-base resize-none focus:ring-1 focus:ring-blue-500 outline-none"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Save Button */}
      <div className="mt-8 flex justify-end">
        <button
          type="button"
          onClick={handleSubmit}
          className="inline-flex items-center gap-2 px-6 py-3 text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Save size={18} />
          Save
        </button>
      </div>
    </div>
  );
};

export default OurFootPrintForm;
